/**
 * Manual chain-switch (SEP 5.b.1 §D-F).
 *
 * ONLY invoked from an explicit user click on the "Switch network"
 * button. NEVER called on mount, on connect, on quote success, or from
 * any useEffect. The purchase panel exposes this via `switchToExpected`.
 *
 * Uses wagmi's `useSwitchChain` — no raw RPC call to
 * `wallet_addEthereumChain` (unauthorized in 5.b.1).
 */
import { useCallback, useState } from "react";
import { useSwitchChain } from "wagmi";

export type ChainSwitchOutcome =
  | { kind: "idle" }
  | { kind: "switching" }
  | { kind: "success" }
  | { kind: "rejected" }
  | { kind: "error"; message: string };

export interface UseManualChainSwitchResult {
  outcome: ChainSwitchOutcome;
  switchToExpected: (expectedChainId: number) => Promise<void>;
  reset: () => void;
}

// Heuristic — wagmi bubbles the wallet's user-rejection error class.
// We don't want to swallow real errors as "rejected", so we branch on
// well-known error names/codes only.
function isUserRejection(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { name?: string; code?: number | string; message?: string };
  if (e.code === 4001 || e.code === "ACTION_REJECTED") return true;
  if (typeof e.name === "string" && /UserRejectedRequestError/i.test(e.name)) {
    return true;
  }
  if (typeof e.message === "string" && /user rejected/i.test(e.message)) {
    return true;
  }
  return false;
}

export function useManualChainSwitch(): UseManualChainSwitchResult {
  const { switchChainAsync } = useSwitchChain();
  const [outcome, setOutcome] = useState<ChainSwitchOutcome>({ kind: "idle" });

  const switchToExpected = useCallback(
    async (expectedChainId: number) => {
      setOutcome({ kind: "switching" });
      try {
        await switchChainAsync({ chainId: expectedChainId });
        setOutcome({ kind: "success" });
      } catch (err) {
        if (isUserRejection(err)) {
          setOutcome({ kind: "rejected" });
        } else {
          const message =
            err instanceof Error ? err.message : String(err);
          setOutcome({ kind: "error", message });
        }
      }
    },
    [switchChainAsync],
  );

  const reset = useCallback(() => setOutcome({ kind: "idle" }), []);
  return { outcome, switchToExpected, reset };
}

// Exported for unit testing without React.
export const __test = { isUserRejection };
